import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppProfile } from '@internal/plugin-waadoo-config-common';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { autheliaAuthApiRef } from '../shared/auth/autheliaAuthApiRef';
import {
  findApplicationForBranch,
  type ArgoApplication,
} from '../projectEnvironments/argoApp';
import { loadProjectApplications } from '../projectEnvironments/projectApplications';
import {
  fetchPipelines,
  pipelineChipStatus,
  type PipelineInfo,
} from '../projectEnvironments/pipelines';
import {
  isApplicationMoving,
  applicationState,
} from '../projectEnvironments/podsFollowUp';
import { runsProjectImage } from '../projectEnvironments/serviceProgress';
import { useProjectAccessState } from '../projectEnvironments/tokenGroups';
import { useVisiblePolling } from '../projectEnvironments/visiblePolling';

/**
 * The « Mise en service » of a project just created: its first build — the
 * latest pipeline of `dev` — and its `dev` environment — the project's
 * ArgoCD Application of branch `dev` —, read through the paths the project
 * page already uses (`fetchPipelines`, `loadProjectApplications`), so the
 * page shares their answers.
 *
 * Both reads work before the catalog has the project: the pipelines route
 * takes any repository URL from a signed-in user (`pipelinesPlugin.ts`,
 * `createLatestHandler`), and the ArgoCD read selects by label, with the
 * user's own id_token. That token is the limit: ArgoCD grants a project from
 * the `groups` of the id_token, which keep those of the sign-in
 * (`tokenGroups.ts`) — the creator, added to the project's groups by the
 * task, sees an empty list until they sign in again. The ArgoCD read
 * waits for the token's groups to be read (`useProjectAccessState`), and is
 * skipped when they lack the project's: the environment stays « en
 * attente ».
 */

/** The branch of the first build and of the environment followed. */
export const FOLLOWED_BRANCH = 'dev';

/** At most one read every 10 s. */
export const FOLLOW_POLL_MS = 10_000;

/**
 * Followed this long after the task ended, then no more: past it, one read
 * on opening the page shows where things stand.
 */
export const FOLLOW_WINDOW_MS = 30 * 60_000;

export type FollowState = 'done' | 'running' | 'pending' | 'failed';

export interface FollowUpView {
  build: FollowState;
  environment: FollowState;
}

/**
 * The environment runs Odoo: Synced, Healthy, not rolling out, and running
 * the project's own image (`runsProjectImage`). Synced and Healthy alone is
 * not enough: the skeleton's `deploy/values-branch.yaml` ships
 * `odoo.enabled: false` until the CI's `promote` job writes the first image
 * tag, so `dev` first syncs EMPTY, Synced and Healthy, while its first build
 * runs.
 */
export function isEnvironmentReady(app: ArgoApplication | undefined): boolean {
  return (
    app !== undefined &&
    app.status?.sync?.status === 'Synced' &&
    app.status.health?.status === 'Healthy' &&
    !isApplicationMoving(applicationState(app)) &&
    runsProjectImage(app)
  );
}

/**
 * The environment of a `standard` project is deployed (point 8): Synced,
 * Healthy, not rolling out, at least one sync done (`status.history`) and
 * images listed (`status.summary.images`) — the backend's `isDeployed`
 * (`environmentPower/argo.ts`). Not `runsProjectImage`: it has no image of
 * its own (the chart's public one).
 */
export function isStandardEnvironmentReady(
  app: ArgoApplication | undefined,
): boolean {
  return (
    app !== undefined &&
    app.status?.sync?.status === 'Synced' &&
    app.status.health?.status === 'Healthy' &&
    !isApplicationMoving(applicationState(app)) &&
    (app.status.history?.length ?? 0) > 0 &&
    (app.status.summary?.images?.length ?? 0) > 0
  );
}

/** The build's state from its pipeline: none read yet is pending. */
function buildFollowState(pipeline: PipelineInfo | undefined): FollowState {
  if (!pipeline) return 'pending';
  switch (pipelineChipStatus(pipeline.status)) {
    case 'healthy':
      return 'done';
    case 'progressing':
      return 'running';
    case 'failed':
      return 'failed';
    default:
      // Canceled, skipped: neither done nor failed.
      return 'pending';
  }
}

/**
 * Where the two milestones stand. The build is its pipeline's, and only its
 * pipeline's: none read yet — the first pipeline is not listed yet, or could
 * not be read — is pending, as a canceled or skipped run. The environment
 * is done once it runs Odoo ({@link isEnvironmentReady}); before, it waits
 * for a successful build — its Application, empty or without its image,
 * says nothing until then —, then deploys.
 */
export function followUpView(
  pipeline: PipelineInfo | undefined,
  app: ArgoApplication | undefined,
  profile?: AppProfile,
): FollowUpView {
  const build = buildFollowState(pipeline);
  if (profile === 'standard') {
    // No build to wait for (point 8): deployed, or deploying.
    return {
      build,
      environment: isStandardEnvironmentReady(app) ? 'done' : 'running',
    };
  }
  if (isEnvironmentReady(app)) {
    return { build, environment: 'done' };
  }
  return { build, environment: build === 'done' ? 'running' : 'pending' };
}

export interface CreationFollowUp {
  pipeline?: PipelineInfo;
  app?: ArgoApplication;
  /** Reading again every {@link FOLLOW_POLL_MS}. */
  following: boolean;
  /** The id_token lacks the project's groups: ArgoCD shows nothing yet. */
  staleAccess: boolean;
}

/**
 * Follows the first build and the `dev` environment of `project`, whose
 * repository is `repoUrl`. Nothing is read without `enabled` (a task not
 * finished, or failed). One read at once; then, while the task ended less
 * than {@link FOLLOW_WINDOW_MS} ago and the environment is not ready nor the
 * build failed, one every {@link FOLLOW_POLL_MS} while the tab is visible
 * (`useVisiblePolling`). A task ended long ago — or whose end is unknown —
 * gets the one read. A read that fails keeps what was known: no error, the
 * milestone stays where it was.
 *
 * `profile`, the project's type's (`useProjectLifecycle`): a `standard` one
 * has no build — no pipeline read — and is ready once deployed
 * (`isStandardEnvironmentReady`); any other, or unknown, as before.
 */
export function useCreationFollowUp(options: {
  project: string;
  repoUrl?: string;
  endedAt?: number;
  enabled: boolean;
  profile?: AppProfile;
}): CreationFollowUp {
  const { project, repoUrl, endedAt, enabled, profile } = options;
  const standard = profile === 'standard';
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const autheliaAuth = useApi(autheliaAuthApiRef);
  const access = useProjectAccessState(enabled ? project : undefined);
  const staleAccess = access === 'stale';
  const [pipeline, setPipeline] = useState<PipelineInfo>();
  const [app, setApp] = useState<ArgoApplication>();
  const [, setExpired] = useState(0);
  const active = useRef(true);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const read = useCallback(async () => {
    const reads: Array<Promise<void>> = [];
    if (repoUrl && !standard) {
      reads.push(
        fetchPipelines({
          fetchApi,
          discoveryApi,
          environments: [{ branch: FOLLOWED_BRANCH, repoUrl }],
        }).then(
          byRef => {
            const latest = byRef[FOLLOWED_BRANCH];
            if (active.current && latest) setPipeline(latest);
          },
          () => {
            // Kept as it was: the milestone links to the pipelines page.
          },
        ),
      );
    }
    // Not before the token's groups are known, nor when they lack the
    // project's: ArgoCD would answer an empty list.
    if (access === 'ok') {
      reads.push(
        loadProjectApplications(
          {
            fetchApi,
            discoveryApi,
            autheliaAuth,
            project,
            noIdentityMessage: 'Aucune session Authelia',
            notJsonMessage: body => `Réponse inattendue d’ArgoCD : ${body}`,
          },
          { maxAgeMs: FOLLOW_POLL_MS },
        ).then(
          apps => {
            const found = findApplicationForBranch(apps, FOLLOWED_BRANCH);
            if (active.current && found) setApp(found);
          },
          () => {
            // Kept as it was: the environment stays where it was.
          },
        ),
      );
    }
    await Promise.all(reads);
  }, [
    repoUrl,
    standard,
    access,
    fetchApi,
    discoveryApi,
    autheliaAuth,
    project,
  ]);

  useEffect(() => {
    if (enabled && access !== 'checking') {
      // `read` handles each of its failures, so it cannot reject.
      void read();
    }
    // Once, when enabled and the token's groups are known.
  }, [enabled, access, read]);

  const deadline =
    endedAt === undefined ? undefined : endedAt + FOLLOW_WINDOW_MS;
  const view = followUpView(pipeline, app, profile);
  const following =
    enabled &&
    deadline !== undefined &&
    Date.now() < deadline &&
    view.environment !== 'done' &&
    view.build !== 'failed';

  // Past the window, render once more: `following` then turns false.
  useEffect(() => {
    if (!following || deadline === undefined) return undefined;
    const timer = setTimeout(
      () => setExpired(count => count + 1),
      Math.max(0, deadline - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [following, deadline]);

  useVisiblePolling(read, FOLLOW_POLL_MS, following && access !== 'checking');

  return {
    ...(pipeline ? { pipeline } : {}),
    ...(app ? { app } : {}),
    following,
    staleAccess,
  };
}
